import { useState, useRef } from "react";
import { useAuth } from "../lib/AuthContext";
import { apiUpload } from "../lib/api";
import { COLORS } from "../lib/colors";

const STATUS_BORDER = {
  waiting: COLORS.border,
  uploading: COLORS.accent,
  retrying: COLORS.purple,
  success: COLORS.green,
  failed: COLORS.red,
};

const STATUS_COLOR = {
  waiting: COLORS.textMuted,
  uploading: COLORS.accent,
  retrying: COLORS.purple,
  success: COLORS.green,
  failed: COLORS.red,
};

const STATUS_LABEL = {
  waiting: "Waiting",
  uploading: "Uploading...",
  retrying: "Retrying...",
  success: "Done",
  failed: "Failed",
};

export default function UploadTab() {
  const { session, schoolId } = useAuth();
  const fileInputRef = useRef(null);

  const [fileItems, setFileItems] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [isDragOver, setIsDragOver] = useState(false);
  const [summary, setSummary] = useState(null);
  const [pdfWarning, setPdfWarning] = useState(false);

  // --- File ingestion ---
  const addFiles = (rawFiles) => {
    let hasPdf = false;
    const incoming = Array.from(rawFiles)
      .filter((f) => {
        const ext = f.name.split(".").pop().toLowerCase();
        if (ext === "pdf") {
          hasPdf = true;
          return false;
        }
        return ext === "docx";
      })
      .filter(
        (f) =>
          !fileItems.some(
            (item) => item.file.name === f.name && item.file.size === f.size
          )
      )
      .map((f) => ({ id: crypto.randomUUID(), file: f, status: "waiting" }));

    if (hasPdf) setPdfWarning(true);
    else setPdfWarning(false);

    if (incoming.length > 0) {
      setFileItems((prev) => [...prev, ...incoming]);
      setSummary(null);
    }
  };

  // --- Single file upload with retry ---
  const uploadItem = async (item, retryCount = 0) => {
    const MAX_RETRIES = 2;

    setFileItems((prev) =>
      prev.map((i) =>
        i.id === item.id
          ? { ...i, status: retryCount > 0 ? "retrying" : "uploading" }
          : i
      )
    );

    const formData = new FormData();
    formData.append("file", item.file);
    formData.append("school_id", schoolId);
    formData.append("candidate_id", item.id);
    formData.append("filename", item.file.name);

    try {
      await apiUpload("/candidates", formData, session.access_token, () => {});
      setFileItems((prev) =>
        prev.map((i) => (i.id === item.id ? { ...i, status: "success" } : i))
      );
      return true;
    } catch {
      if (retryCount < MAX_RETRIES) {
        await new Promise((r) => setTimeout(r, 1000 * (retryCount + 1)));
        return uploadItem(item, retryCount + 1);
      }
      setFileItems((prev) =>
        prev.map((i) => (i.id === item.id ? { ...i, status: "failed" } : i))
      );
      return false;
    }
  };

  // --- Upload loop (shared by Submit and Retry) ---
  const runUpload = async (itemsToProcess) => {
    setUploading(true);
    setProgress(0);
    setSummary(null);

    let completed = 0;
    for (const item of itemsToProcess) {
      await uploadItem(item);
      completed++;
      setProgress(completed / itemsToProcess.length);
    }

    setUploading(false);
    setFileItems((prev) => {
      const success = prev.filter((i) => i.status === "success").length;
      const failed = prev.filter((i) => i.status === "failed").length;
      setSummary({ total: prev.length, success, failed });
      return prev;
    });
  };

  const handleUpload = () => {
    if (!schoolId) {
      setSummary({ error: "No school ID found. Contact your admin." });
      return;
    }
    const toUpload = fileItems.filter(
      (i) => i.status === "waiting" || i.status === "failed"
    );
    if (!toUpload.length || uploading) return;
    runUpload(toUpload);
  };

  const handleRetryFailed = () => {
    const failedItems = fileItems.filter((i) => i.status === "failed");
    if (!failedItems.length) return;
    setFileItems((prev) =>
      prev.map((i) => (i.status === "failed" ? { ...i, status: "waiting" } : i))
    );
    runUpload(failedItems.map((i) => ({ ...i, status: "waiting" })));
  };

  const handleClear = () => {
    setFileItems([]);
    setSummary(null);
    setProgress(0);
    setPdfWarning(false);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const uploadableCount = fileItems.filter(
    (i) => i.status === "waiting" || i.status === "failed"
  ).length;

  return (
    <div>
      <h2
        style={{
          fontSize: 20,
          fontWeight: 700,
          color: COLORS.text,
          marginBottom: 4,
        }}
      >
        Upload Student Profiles
      </h2>
      <p
        style={{
          fontSize: 13,
          color: COLORS.textMuted,
          marginBottom: 24,
        }}
      >
        Upload Allsorter-formatted CVs (DOCX) to add students to the talent
        pool. DOCX preserves data structure for accurate extraction.
      </p>

      {/* PDF warning */}
      {pdfWarning && (
        <div
          style={{
            padding: "10px 14px",
            borderRadius: 8,
            marginBottom: 16,
            backgroundColor: COLORS.orangeLight,
            color: COLORS.orange,
            fontSize: 13,
          }}
        >
          PDF files were skipped. Please upload DOCX files — they preserve data
          structure for accurate extraction. Use the DOCX version from Allsorter.
        </div>
      )}

      {/* Drop zone */}
      <div
        onDragOver={(e) => {
          e.preventDefault();
          setIsDragOver(true);
        }}
        onDragEnter={(e) => {
          e.preventDefault();
          setIsDragOver(true);
        }}
        onDragLeave={() => setIsDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setIsDragOver(false);
          addFiles(e.dataTransfer.files);
        }}
        onClick={() => fileInputRef.current?.click()}
        style={{
          backgroundColor: isDragOver ? COLORS.accentLight : COLORS.surface,
          border: `2px dashed ${isDragOver ? COLORS.accent : COLORS.border}`,
          borderRadius: 10,
          padding: 32,
          textAlign: "center",
          marginBottom: 20,
          cursor: "pointer",
          transition: "all 0.2s",
        }}
      >
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept=".docx"
          onChange={(e) => {
            addFiles(e.target.files);
            e.target.value = "";
          }}
          style={{ display: "none" }}
        />
        <p
          style={{
            fontSize: 14,
            color: isDragOver ? COLORS.accent : COLORS.textSecondary,
            marginBottom: 8,
            fontWeight: 500,
          }}
        >
          {isDragOver
            ? "Drop files here"
            : "Drop DOCX files here or click to browse"}
        </p>
        <p style={{ fontSize: 12, color: COLORS.textMuted }}>
          DOCX files only — Allsorter formatted CVs
        </p>
      </div>

      {/* File list */}
      {fileItems.length > 0 && (
        <div style={{ marginBottom: 20 }}>
          {fileItems.map((item) => {
            const sizeKB = (item.file.size / 1024).toFixed(0);
            return (
              <div
                key={item.id}
                style={{
                  display: "flex",
                  alignItems: "center",
                  padding: "10px 14px",
                  borderRadius: 8,
                  border: `1px solid ${STATUS_BORDER[item.status]}`,
                  marginBottom: 8,
                  backgroundColor: COLORS.surface,
                  transition: "border-color 0.2s",
                }}
              >
                {/* Type badge */}
                <div
                  style={{
                    width: 34,
                    height: 34,
                    borderRadius: 6,
                    flexShrink: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 11,
                    fontWeight: 700,
                    backgroundColor: COLORS.accentLight,
                    color: COLORS.accent,
                  }}
                >
                  DOCX
                </div>

                {/* Name + size */}
                <div style={{ flex: 1, minWidth: 0, margin: "0 12px" }}>
                  <div
                    style={{
                      fontSize: 13,
                      fontWeight: 500,
                      color: COLORS.text,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {item.file.name}
                  </div>
                  <div style={{ fontSize: 12, color: COLORS.textMuted }}>
                    {sizeKB} KB
                  </div>
                </div>

                {/* Status */}
                <span
                  style={{
                    fontSize: 12,
                    fontWeight: 600,
                    flexShrink: 0,
                    color: STATUS_COLOR[item.status],
                  }}
                >
                  {item.status === "success" ? "\u2713 " : ""}
                  {STATUS_LABEL[item.status]}
                </span>

                {/* Remove button — only while waiting */}
                {item.status === "waiting" && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setFileItems((prev) =>
                        prev.filter((i) => i.id !== item.id)
                      );
                    }}
                    style={{
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      color: COLORS.textMuted,
                      fontSize: 18,
                      padding: "0 0 0 10px",
                      lineHeight: 1,
                      fontFamily: "inherit",
                    }}
                    aria-label="Remove file"
                  >
                    \u00d7
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Progress bar */}
      {uploading && (
        <div style={{ marginBottom: 16 }}>
          <div
            style={{
              height: 8,
              borderRadius: 4,
              backgroundColor: COLORS.surfaceAlt,
              overflow: "hidden",
            }}
          >
            <div
              style={{
                height: "100%",
                width: `${progress * 100}%`,
                backgroundColor: COLORS.accent,
                borderRadius: 4,
                transition: "width 0.3s",
              }}
            />
          </div>
        </div>
      )}

      {/* Summary stats */}
      {summary && !summary.error && (
        <div
          style={{
            display: "flex",
            gap: 24,
            padding: "16px 20px",
            borderRadius: 8,
            marginBottom: 20,
            backgroundColor: COLORS.surface,
            border: `1px solid ${COLORS.border}`,
          }}
        >
          <div style={{ fontSize: 13, color: COLORS.textSecondary }}>
            <div
              style={{ fontSize: 22, fontWeight: 700, color: COLORS.text }}
            >
              {summary.total}
            </div>
            Total
          </div>
          <div style={{ fontSize: 13, color: COLORS.textSecondary }}>
            <div
              style={{ fontSize: 22, fontWeight: 700, color: COLORS.green }}
            >
              {summary.success}
            </div>
            Uploaded
          </div>
          {summary.failed > 0 && (
            <div style={{ fontSize: 13, color: COLORS.textSecondary }}>
              <div
                style={{ fontSize: 22, fontWeight: 700, color: COLORS.red }}
              >
                {summary.failed}
              </div>
              Failed
            </div>
          )}
        </div>
      )}

      {/* Error banner */}
      {summary?.error && (
        <div
          style={{
            padding: "10px 14px",
            borderRadius: 8,
            marginBottom: 16,
            backgroundColor: COLORS.redLight,
            color: COLORS.red,
            fontSize: 13,
          }}
        >
          {summary.error}
        </div>
      )}

      {/* Button row */}
      <div style={{ display: "flex", gap: 10 }}>
        <button
          onClick={handleUpload}
          disabled={uploading || uploadableCount === 0}
          style={{
            flex: 1,
            padding: "11px 0",
            borderRadius: 8,
            border: "none",
            backgroundColor:
              uploadableCount > 0 ? COLORS.accent : COLORS.surfaceAlt,
            color: uploadableCount > 0 ? "#fff" : COLORS.textMuted,
            fontSize: 14,
            fontWeight: 600,
            fontFamily: "inherit",
            cursor:
              uploading || uploadableCount === 0 ? "not-allowed" : "pointer",
            opacity: uploading ? 0.7 : 1,
            transition: "all 0.15s",
          }}
        >
          {uploading ? "Uploading..." : "Submit Profiles"}
        </button>

        {summary?.failed > 0 && !uploading && (
          <button
            onClick={handleRetryFailed}
            style={{
              padding: "11px 20px",
              borderRadius: 8,
              border: `1px solid ${COLORS.border}`,
              backgroundColor: COLORS.surface,
              color: COLORS.text,
              fontSize: 14,
              fontWeight: 500,
              fontFamily: "inherit",
              cursor: "pointer",
            }}
          >
            Retry Failed
          </button>
        )}

        {fileItems.length > 0 && !uploading && (
          <button
            onClick={handleClear}
            style={{
              padding: "11px 20px",
              borderRadius: 8,
              border: `1px solid ${COLORS.border}`,
              backgroundColor: "transparent",
              color: COLORS.textSecondary,
              fontSize: 14,
              fontFamily: "inherit",
              cursor: "pointer",
            }}
          >
            Clear
          </button>
        )}
      </div>
    </div>
  );
}
